"use client"

import { useState } from "react"

import {
  Box,
  useDisclosure,
  useToast,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Icon,
  Flex,
  Spinner,
} from "@chakra-ui/react"
import { FaUpload } from "react-icons/fa"
import { useDropzone } from "react-dropzone"
import * as XLSX from "xlsx"
import ImportProcessor, { type ImportStep, type TabData } from "@/components/ImportExel"
import { Example } from "@/components/example"
import { api } from "@/services/api"
import { queryClient } from "@/App"

export interface ImportDepositoData {
  depositoerp?: number | string
  nome?: string
  tipo?: number | string
  ativo?: boolean | string
}

interface DepositoImportProps {
  onImportComplete?: () => void
  triggerSubmitImport: (data: ImportDepositoData[]) => Promise<any>
  setFileContentForProcess: (data: ImportDepositoData[]) => void
}

export const DepositoImport = ({
  onImportComplete,
  triggerSubmitImport,
  setFileContentForProcess,
}: DepositoImportProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const toast = useToast()
  const [importData, setImportData] = useState<ImportDepositoData[]>([])
  const [isProcessing, setIsProcessing] = useState(false)
  const [isLoadingFile, setIsLoadingFile] = useState(false)

  const steps: ImportStep[] = [
    { title: "Validação", description: "Verificando estrutura dos dados" },
    { title: "Processamento", description: "Cadastrando/Atualizando depósitos" },
    { title: "Finalização", description: "Concluindo importação" },
  ]

  const formatterStringToBoolean = (value: string | boolean | undefined | null): boolean => {
    if (value === undefined || value === null || String(value).trim() === "") {
      return true
    }

    if (typeof value === "boolean") {
      return value
    }

    const lowerCaseString = String(value).toLowerCase()
    return (
      lowerCaseString === "true" ||
      lowerCaseString === "1" ||
      lowerCaseString === "verdadeiro" ||
      lowerCaseString === "sim"
    )
  }

  // Função para validar e converter números de forma segura
  const safeNumberConversion = (value: any): number | null => {
    if (value === undefined || value === null || value === "") {
      return null
    }
    const numValue = Number(value)
    if (isNaN(numValue)) {
      return null
    }
    return numValue
  }

  const parseCSV = (content: string): ImportDepositoData[] => {
    try {
      const lines = content.split("\n")
      if (lines.length < 2) throw new Error("CSV inválido ou vazio")

      const headers = lines[0].split(",").map((h) => h.trim().toLowerCase())

      return lines
        .slice(1)
        .filter((line) => line.trim())
        .map((line) => {
          const values = line.split(",").map((v) => v.trim())
          const item: Record<string, any> = {}

          headers.forEach((header, index) => {
            if (index < values.length) {
              const value = values[index]
              if (header === "depositoerp" || header === "tipo") {
                item[header] = safeNumberConversion(value)
              } else if (header === "ativo") {
                item[header] = formatterStringToBoolean(value === "" ? undefined : value)
              } else {
                item[header] = value
              }
            }
          })

          return item as ImportDepositoData
        })
    } catch (error) {
      console.error("Erro ao analisar CSV:", error)
      throw new Error("Formato CSV inválido para depósitos")
    }
  }

  const parseXLSX = async (file: File): Promise<ImportDepositoData[]> => {
    try {
      setIsLoadingFile(true)
      return new Promise<ImportDepositoData[]>((resolve, reject) => {
        const reader = new FileReader()

        reader.onload = (e) => {
          try {
            const data = e.target?.result
            if (!data) {
              reject(new Error("Falha ao ler arquivo Excel"))
              return
            }

            const workbook = XLSX.read(data, { type: "array" })
            const firstSheetName = workbook.SheetNames[0]
            const worksheet = workbook.Sheets[firstSheetName]
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 })

            if (jsonData.length < 2) {
              reject(new Error("Planilha vazia ou sem dados"))
              return
            }

            const headers = (jsonData[0] as string[]).map((h) => h.trim().toLowerCase())
            const rows = jsonData.slice(1) as any[][]

            const result = rows.map((row) => {
              const item: Record<string, any> = {}
              headers.forEach((header, index) => {
                if (index < row.length) {
                  const value = row[index]
                  if (header === "depositoerp" || header === "tipo") {
                    item[header] = safeNumberConversion(value)
                  } else if (header === "ativo") {
                    item[header] = formatterStringToBoolean(value)
                  } else {
                    item[header] = value
                  }
                }
              })
              return item as ImportDepositoData
            })

            resolve(result)
          } catch (error) {
            console.error("Erro ao processar Excel:", error)
            reject(new Error("Falha ao processar arquivo Excel"))
          } finally {
            setIsLoadingFile(false)
          }
        }

        reader.onerror = () => {
          setIsLoadingFile(false)
          reject(new Error("Erro ao ler arquivo Excel"))
        }

        reader.readAsArrayBuffer(file)
      })
    } catch (error) {
      setIsLoadingFile(false)
      throw error
    }
  }

  const parseFileContent = (content: string): ImportDepositoData[] => {
    try {
      return JSON.parse(content)
    } catch (jsonError) {
      try {
        return parseCSV(content)
      } catch (csvError) {
        console.error("Failed to parse file as JSON or CSV:", csvError)
        throw new Error("Formato de arquivo não suportado")
      }
    }
  }

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      "application/json": [".json"],
      "text/csv": [".csv"],
      "application/vnd.ms-excel": [".xls"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
      "text/plain": [".txt"],
    },
    onDrop: async (acceptedFiles) => {
      try {
        const file = acceptedFiles[0]
        if (!file) return

        if (file.size > 10 * 1024 * 1024) {
          toast({
            title: "Arquivo muito grande",
            description: "O tamanho máximo permitido é 10MB.",
            status: "error",
            duration: 5000,
            isClosable: true,
          })
          return
        }

        if (
          file.type === "application/vnd.ms-excel" ||
          file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
          file.name.endsWith(".xls") ||
          file.name.endsWith(".xlsx")
        ) {
          try {
            const excelData = await parseXLSX(file)
            if (excelData.length === 0) {
              toast({
                title: "Arquivo vazio",
                description: "A planilha não contém dados para importação.",
                status: "warning",
                duration: 5000,
                isClosable: true,
              })
              return
            }

            setImportData(excelData)
            setFileContentForProcess(excelData)
            onOpen()
            return
          } catch (error) {
            console.error("Excel parsing error:", error)
            toast({
              title: "Erro ao processar planilha",
              description: error instanceof Error ? error.message : "Formato de planilha inválido",
              status: "error",
              duration: 5000,
              isClosable: true,
            })
            return
          }
        }

        const reader = new FileReader()
        reader.onerror = () => {
          console.error("FileReader error:", reader.error)
          toast({
            title: "Erro ao ler arquivo",
            description: "Não foi possível ler o conteúdo do arquivo.",
            status: "error",
            duration: 5000,
            isClosable: true,
          })
        }

        reader.onload = async (event) => {
          try {
            const result = event.target?.result
            if (typeof result === "string") {
              const parsedData = parseFileContent(result)
              const dataArray = Array.isArray(parsedData) ? parsedData : [parsedData]

              if (dataArray.length === 0) {
                toast({
                  title: "Arquivo vazio",
                  description: "O arquivo não contém dados para importação.",
                  status: "warning",
                  duration: 5000,
                  isClosable: true,
                })
                return
              }

              setImportData(dataArray)
              setFileContentForProcess(dataArray)
              onOpen()
            } else {
              console.error("File result is not a string:", typeof result)
              toast({
                title: "Formato inválido",
                description: "O conteúdo do arquivo não é texto válido.",
                status: "error",
                duration: 5000,
                isClosable: true,
              })
            }
          } catch (error) {
            console.error("Error processing file:", error)
            toast({
              title: "Erro ao processar arquivo",
              description: "O formato do arquivo não é válido. Por favor, verifique o arquivo e tente novamente.",
              status: "error",
              duration: 5000,
              isClosable: true,
            })
          }
        }

        reader.readAsText(file)
      } catch (error) {
        console.error("Dropzone error:", error)
        toast({
          title: "Erro ao ler arquivo",
          description: "Ocorreu um erro ao ler o arquivo. Por favor, tente novamente.",
          status: "error",
          duration: 5000,
          isClosable: true,
        })
      }
    },
  })

  const tabs: TabData[] = [
    {
      id: "depositos",
      title: "Depósitos",
      metadata: {
        totalRows: importData.length,
      },
      content: (
        <TableContainer>
          <Table variant="simple" size="sm">
            <Thead>
              <Tr>
                <Th textAlign="center">Código ERP</Th>
                <Th textAlign="center">Nome</Th>
                <Th textAlign="center">Tipo</Th>
                <Th textAlign="center">Ativo</Th>
              </Tr>
            </Thead>
            <Tbody>
              {importData.map((item, index) => (
                <Tr key={index}>
                  <Td textAlign="center">{item.depositoerp}</Td>
                  <Td textAlign="center">{item.nome}</Td>
                  <Td textAlign="center">{item.tipo}</Td>
                  <Td textAlign="center">
                    <Flex align="center" justify="center">
                      <Box
                        w="8px"
                        h="8px"
                        borderRadius="full"
                        bg={item.ativo ? "green.400" : "gray.300"}
                        boxShadow={item.ativo ? "0 0 0 2px rgba(72, 187, 120, 0.2)" : "none"}
                        transition="all 0.2s"
                      />
                    </Flex>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      ),
    },
  ]

  const processImportData = async (
    data: ImportDepositoData[],
    addLog: (message: string, type: "info" | "success" | "warning" | "error", rowData?: any, rowIndex?: number) => void,
    updateStats: (key: string, increment?: number) => void,
    updateProgress: (tabId: string, count: number) => void,
    setCurrentStep: (step: number) => void,
  ) => {
    try {
      setIsProcessing(true)
      setCurrentStep(0)
      addLog("Iniciando validação dos dados dos depósitos...", "info")

      const validDepositos: ImportDepositoData[] = []
      let errorsCount = 0

      for (let i = 0; i < data.length; i++) {
        const item = data[i]

        // Validação mais rigorosa dos campos numéricos
        const depositoerpValue = safeNumberConversion(item.depositoerp)
        const tipoValue = safeNumberConversion(item.tipo)

        if (depositoerpValue === null || depositoerpValue <= 0) {
          addLog(
            `Linha ${i + 1}: Código ERP do depósito inválido ou não especificado. Item ignorado.`,
            "error",
            item,
            i,
          )
          errorsCount++
          continue
        }

        if (!item.nome || item.nome.trim() === "") {
          addLog(`Linha ${i + 1}: Nome do depósito não especificado. Item ignorado.`, "error", item, i)
          errorsCount++
          continue
        }

        if (tipoValue === null || tipoValue <= 0) {
          addLog(`Linha ${i + 1}: Tipo de depósito inválido ou não especificado. Item ignorado.`, "error", item, i)
          errorsCount++
          continue
        }

        const parsedItem: ImportDepositoData = {
          ...item,
          depositoerp: depositoerpValue,
          tipo: tipoValue,
          ativo: formatterStringToBoolean(item.ativo),
        }

        validDepositos.push(parsedItem)
        addLog(`Linha ${i + 1}: Validado com sucesso.`, "success")
        updateProgress("depositos", i + 1)
      }

      if (validDepositos.length === 0) {
        addLog("Nenhum depósito válido para processar.", "warning")
        updateStats("errors", errorsCount)
        return
      }

      updateStats("totalValidado", validDepositos.length)
      updateStats("errors", errorsCount)

      setCurrentStep(1)
      addLog("Iniciando envio dos depósitos...", "info")

      let sucessCount = 0
      let errorCount = 0

      // Processar cada item individualmente
      for (let i = 0; i < validDepositos.length; i++) {
        const item = validDepositos[i]

        try {
          const body = {
            depositoerp: item.depositoerp,
            nome: item.nome,
            tipo: item.tipo,
            ativo: item.ativo,
          }

          await api.post("/endereco/deposito", body)
          addLog(
            `Depósito ${i + 1}/${validDepositos.length}: Processado com sucesso (ERP: ${item.depositoerp}, Nome: ${item.nome})`,
            "success",
          )
          sucessCount++
          updateProgress("depositos", i + 1)
        } catch (error: any) {
          errorCount++
          const errorMessage = `Depósito ${i + 1}/${validDepositos.length}: Erro do servidor (${error.response?.status}): ${error.response?.data?.mensagem || "Erro desconhecido"}`
          addLog(errorMessage, "error", item, i)

          if (error.response) {
            const errorData = error.response.data
            if (errorData.timestamp) {
              addLog(`Timestamp: ${errorData.timestamp}`, "info")
            }
            if (errorData.errors && Array.isArray(errorData.errors)) {
              errorData.errors.forEach((err: { field: string; message: string }) => {
                addLog(`Erro no campo "${err.field}": ${err.message}`, "error")
              })
            }
            console.error("Error response:", error.response)
          } else if (error.request) {
            addLog(`Depósito ${i + 1}/${validDepositos.length}: Erro - Não foi recebida resposta do servidor.`, "error")
            console.error("Error request:", error.request)
          } else {
            addLog(
              `Depósito ${i + 1}/${validDepositos.length}: Erro - ${error.message || "Erro desconhecido"}`,
              "error",
            )
            console.error("Error message:", error.message)
          }
        }
      }

      updateStats("totalProcessado", sucessCount)
      updateStats("depositosCriados", sucessCount)
      updateStats("errors", errorCount)

      setCurrentStep(2)

      if (sucessCount > 0) {
        addLog(
          `Processamento concluído: ${sucessCount} depósitos processados com sucesso${errorCount > 0 ? `, ${errorCount} com erro` : ""}.`,
          "success",
        )


        if (typeof onImportComplete === "function") {
          queryClient.invalidateQueries(["endereco/deposito-list"])
        }
      } else {
        addLog("Processamento concluído: Nenhum depósito foi processado devido a erros.", "warning")
      }
    } catch (error) {
      addLog("Erro geral durante a importação.", "error")
      updateStats("errors", 1)
    } finally {
      setIsProcessing(false)
    }
  }

  const initialStats = {
    totalValidado: 0,
    depositosCriados: 0,
    errors: 0,
  }

  return (
    <>
      <Box
        {...getRootProps()}
        p={4}
        border="2px dashed"
        borderColor={isDragActive ? "blue.500" : "gray.300"}
        borderRadius="md"
        textAlign="center"
        cursor="pointer"
        transition="all 0.2s"
        _hover={{ borderColor: "blue.400", bg: "blue.50" }}
        position="relative"
      >
        <input {...getInputProps()} />
        {isLoadingFile ? (
          <Flex direction="column" align="center" justify="center">
            <Spinner size="md" color="blue.500" mb={2} />
            <Text>Processando arquivo...</Text>
          </Flex>
        ) : (
          <>
            <Icon as={FaUpload} w={8} h={8} color="blue.500" mb={2} />
            <Text fontWeight="medium">
              {isDragActive ? "Solte o arquivo aqui..." : "Arraste e solte um arquivo ou clique para selecionar"}
            </Text>
            <Text fontSize="sm" color="gray.500" mt={1}>
              Formatos suportados: CSV, XLSX, XLS
            </Text>
          </>
        )}
      </Box>

      <Flex mt={2} gap={2}>
        <Example
          fileFormater={`
            {
              "depositoerp": 101,
              "nome": "Depósito Central",
              "tipo": 1,
              "ativo": true
            }
        `}
        />
      </Flex>

      <ImportProcessor
        title="Importação de Depósitos"
        subtitle={`${importData.length} depósitos`}
        data={importData}
        steps={steps}
        tabs={tabs}
        processFunction={processImportData}
        initialStats={initialStats}
        actionButtonText="Processar Importação"
        isOpen={isOpen}
        onClose={() => {
          onClose()
          if (!isProcessing) {
            setImportData([])
          }
        }}
      />
    </>
  )
}
